/*
  Discount codes — shared maths and wording.

  These mirror public.coupon_discount_amount() in
  backend/sql/coupons.sql. The database is the authority: the
  order_coupon_trg trigger recomputes the discount when the order is
  written and overwrites whatever the browser sent. What lives here is
  only so the cart can retotal instantly when a quantity changes,
  without a round trip for every keystroke — so the two must agree to
  the rupee, or the customer sees one number and pays another.

  A `coupon` here is the snapshot validate_coupon() returned:
    { code, description, discount_type, discount_value,
      max_discount, min_order_amount }
*/

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export const normaliseCode = (code) =>
  String(code || '').trim().toUpperCase();

/** Just the rule out of a validate_coupon() reply — the transient bits
    (valid, reason, message, the discount for one particular subtotal)
    have no business being cached in localStorage. */
export const couponRule = (result) => ({
  code: result?.code || '',
  description: result?.description || null,
  discount_type: result?.discount_type || 'percent',
  discount_value: Number(result?.discount_value) || 0,
  max_discount:
    result?.max_discount === null || result?.max_discount === undefined
      ? null
      : Number(result.max_discount),
  min_order_amount: Number(result?.min_order_amount) || 0,
});

/** What this coupon takes off an order of `subtotal`. Never negative,
    and never more than the order is worth. */
export const couponDiscount = (coupon, subtotal) => {
  if (!coupon) return 0;

  const amount = Math.max(Number(subtotal) || 0, 0);
  const value = Number(coupon.discount_value) || 0;
  if (amount <= 0 || value <= 0) return 0;

  let discount;

  if (coupon.discount_type === 'fixed') {
    discount = value;
  } else {
    discount = (amount * value) / 100;

    // A cap of null or 0 means the percentage runs uncapped.
    const cap = Number(coupon.max_discount);
    if (Number.isFinite(cap) && cap > 0) discount = Math.min(discount, cap);
  }

  return Math.min(Math.max(Math.round(discount), 0), amount);
};

/** Big enough an order for the code to apply? */
export const meetsMinimum = (coupon, subtotal) =>
  (Number(subtotal) || 0) >= Number(coupon?.min_order_amount || 0);

/** "10% off", "10% off up to ₹500", "₹200 off" */
export const describeCoupon = (coupon) => {
  if (!coupon) return '';

  const value = Number(coupon.discount_value) || 0;

  if (coupon.discount_type === 'fixed') return `${inr(value)} off`;

  const cap = Number(coupon.max_discount);
  return Number.isFinite(cap) && cap > 0
    ? `${value}% off up to ${inr(cap)}`
    : `${value}% off`;
};

/** Shown when the cart drops below a code's minimum after an edit. */
export const shortfallMessage = (coupon, subtotal) => {
  const minimum = Number(coupon?.min_order_amount || 0);
  const short = minimum - (Number(subtotal) || 0);

  return short > 0
    ? `Add ${inr(short)} more to use ${coupon.code} (minimum ${inr(minimum)}).`
    : '';
};
